// src/App.jsx - Dashboard component (works in both Electron and Web)

import React, { useState, useEffect } from 'react';
import { AppProvider, useAppContext } from './context/AppContext.jsx';
import { api } from './api/reportingApi.js';

// UI Components
import { LoadingSpinner } from './components/ui/LoadingSpinner.jsx';
import { NotificationContainer } from './components/ui/NotificationContainer.jsx';
import { FilterPanel } from './components/filters/FilterPanel.jsx';

// Tab Components
import { OverviewTab } from './tabs/OverviewTab.jsx';
import { RawInspectionsTab } from './tabs/RawInspectionsTab.jsx';
import { RootCauseAnalysisTab } from './tabs/RootCauseAnalysisTab.jsx';
import { PredictiveInsightsTab } from './tabs/PredictiveInsightsTab.jsx';
import { ModelPerformanceTab } from './tabs/ModelPerformanceTab.jsx';
import { StationsTrendsTab } from './tabs/StationsTrendsTab.jsx';
import { ProjectTrendsTab } from './tabs/ProjectTrendsTab.jsx';
import { LotsTrendsTab } from './tabs/LotsTrendsTab.jsx';
import { SessionsTrendsTab } from './tabs/SessionsTrendsTab.jsx';
import { ConfigurationsTab } from './tabs/ConfigurationsTab.jsx';

/*************************************************************
 * Tab Navigation Component
 *************************************************************/
const TabNavigation = ({ activeTab, onTabChange, showLogout = false }) => {
    const [openDropdown, setOpenDropdown] = useState(null);

    const handleDropdownToggle = (dropdown) => {
        setOpenDropdown(openDropdown === dropdown ? null : dropdown);
    };
    
    const handleTabClick = (tabId) => {
        onTabChange(tabId);
        setOpenDropdown(null);
    };

    const handleLogout = () => {
        if (window.confirm('Are you sure you want to logout?')) {
            // Clear tokens
            localStorage.removeItem('access_token');
            localStorage.removeItem('user');
            // Redirect to login
            window.location.href = '/login';
        }
    };

    const tabs = [
        { id: 'dashboard', label: 'Overview', icon: 'fas fa-chart-line' },
        { 
            id: 'data-explorer', 
            label: 'Performance Trends', 
            icon: 'fas fa-search',
            children: [
                { id: 'projects', label: 'Parts' },
                { id: 'stations', label: 'Stations' },
                { id: 'lots', label: 'Lots' },
                { id: 'sessions', label: 'Sessions' },
            ]
        },
        { id: 'inspections', label: 'Raw Inspection Data', icon: 'fas fa-microscope' },
        { id: 'settings', label: 'Configuration', icon: 'fas fa-cog' },
    ];

    return (
        <div className="d-flex justify-content-between align-items-center mb-3">
            <ul className="nav nav-tabs flex-grow-1" role="tablist">
                {tabs.map(tab => {
                    if (tab.children) {
                        return (
                            <li key={tab.id} className="nav-item dropdown">
                                <a
                                    className={`nav-link dropdown-toggle ${openDropdown === tab.id ? 'show' : ''}`}
                                    onClick={(e) => { 
                                        e.preventDefault();
                                        handleDropdownToggle(tab.id);
                                    }}
                                    href="#"
                                    role="button"
                                    aria-expanded={openDropdown === tab.id}
                                >
                                    <i className={`${tab.icon} me-2`}></i>
                                    {tab.label}
                                </a>
                                <div className={`dropdown-menu ${openDropdown === tab.id ? 'show' : ''}`}>
                                    {tab.children.map(child => (
                                        <a
                                            key={child.id}
                                            className={`dropdown-item ${activeTab === child.id ? 'active' : ''}`}
                                            href={`#${child.id}`}
                                            onClick={(e) => { 
                                                e.preventDefault();
                                                handleTabClick(child.id);
                                            }}
                                        >
                                            {child.label}
                                        </a>
                                    ))}
                                </div>
                            </li>
                        );
                    }
                    
                    return (
                        <li key={tab.id} className="nav-item">
                            <a
                                className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                                href={`#${tab.id}`}
                                onClick={(e) => { 
                                    e.preventDefault();
                                    handleTabClick(tab.id);
                                }}
                            >
                                <i className={`${tab.icon} me-2`}></i>
                                {tab.label}
                            </a>
                        </li>
                    );
                })}
            </ul>
            {showLogout && (
                <button 
                    className="btn btn-outline-danger btn-sm ms-3"
                    onClick={handleLogout}
                    title="Logout"
                >
                    <i className="fas fa-sign-out-alt me-2"></i>
                    Logout
                </button>
            )}
        </div>
    );
};

/*************************************************************
 * Main Application Component
 *************************************************************/
const MultiReportsApp = ({ showLogout }) => {
    const { state, setCurrentTab, updateFilters, removeNotification } = useAppContext();

    // Auto-refresh functionality
    useEffect(() => {
        if (state.preferences.autoRefresh) {
            const interval = setInterval(() => {
                console.log('[App] Auto-refresh triggered');
                window.location.reload();
            }, state.preferences.refreshInterval * 1000);

            return () => {
                console.log('[App] Auto-refresh interval cleared');
                clearInterval(interval);
            };
        }
    }, [state.preferences.autoRefresh, state.preferences.refreshInterval]);

    const renderTabContent = () => {
        console.log('[App] Rendering tab content for:', state.currentTab);
        
        switch (state.currentTab) {
            case 'dashboard':
                return <OverviewTab />;
            case 'inspections':
                return <RawInspectionsTab />;
            case 'root-cause':
                return <RootCauseAnalysisTab />;
            case 'predictive-insights':
                return <PredictiveInsightsTab />;
            case 'model-performance':
                return <ModelPerformanceTab />;
            case 'stations':
                return <StationsTrendsTab />;
            case 'projects':
                return <ProjectTrendsTab />;
            case 'lots':
                return <LotsTrendsTab />;
            case 'sessions':
                return <SessionsTrendsTab />;
            case 'settings':
                return <ConfigurationsTab />;
            default:
                console.warn('[App] Unknown tab:', state.currentTab, 'defaulting to dashboard');
                return <OverviewTab />;
        }
    };

    const showFilters = ['dashboard', 'inspections'].includes(state.currentTab);
    const showSortByForFilterPanel = state.currentTab === 'inspections';

    return (
        <div className="container-fluid py-4">
            <TabNavigation 
                activeTab={state.currentTab} 
                onTabChange={setCurrentTab}
                showLogout={showLogout}
            />
            <div className="mt-3">
                {/* Page titles */}
                {state.currentTab === 'dashboard' && (
                    <div className="mb-4">
                        <h4 className="mb-1">Analytics Dashboard</h4>
                        <p className="text-muted mb-0">
                            Real-time inspection monitoring and quality insights
                        </p>
                    </div>
                )}
                {state.currentTab === 'inspections' && (
                    <div className="mb-4">
                        <h4 className="mb-1">Recent Inspections</h4>
                        <p className="text-muted mb-0">Browse, filter, and review individual inspection records.</p>
                    </div>
                )}

                {showFilters && (
                    <FilterPanel
                        filters={state.filters}
                        onFiltersChange={updateFilters}
                        showSortBy={showSortByForFilterPanel}
                    />
                )}
                {renderTabContent()}
                <NotificationContainer
                    notifications={state.notifications}
                    onRemove={removeNotification}
                />
            </div>
        </div>
    );
};

/*************************************************************
 * Error Boundary Class
 *************************************************************/
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('[ErrorBoundary] Component error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="alert alert-danger text-center m-4">
                    <i className="fas fa-exclamation-triangle fa-4x mb-4"></i>
                    <h3>Application Error</h3>
                    <p className="mb-4">Something went wrong in the React application.</p>
                    <details className="text-start mb-4">
                        <summary className="btn btn-outline-danger mb-2">
                            View Error Details
                        </summary>
                        <pre className="small">{this.state.error?.toString()}</pre>
                    </details>
                    <button
                        className="btn btn-danger btn-lg"
                        onClick={() => window.location.reload()}
                    >
                        <i className="fas fa-redo me-2"></i>
                        Reload Application
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

/*************************************************************
 * Main App Component
 *************************************************************/
const App = ({ showLogout = true }) => {
    // Get user from localStorage if available (web mode)
    // Otherwise use default (Electron mode)
    const storedUser = localStorage.getItem('user');
    const userData = storedUser ? JSON.parse(storedUser) : {
        username: 'local-user',
        role: 'admin'
    };
    
    console.log('[App] Rendering dashboard for user:', userData);
    
    return (
        <ErrorBoundary>
            <AppProvider userData={userData}>
                <MultiReportsApp showLogout={showLogout} />
            </AppProvider>
        </ErrorBoundary>
    );
};

// Global debug helpers
window.analyticsDebug = {
    api: api,
    getApiStats: () => api.getStats(),
    clearApiCache: () => api.clearCache(),
    version: '2.0.0-simple'
};

export default App;